import React from "react"
import { useIntl } from "react-intl"
import { Box, Typography } from "@carrotfertility/carotene-core"
import { AppAccessPageLayout } from "#/components/access-page/AppAccessPageLayout"
import { AppAccessPageCard } from "#/components/access-page/AppAccessPageCard"
import {
  lapsedExternalMemberNoUrlMessage,
  sendLapsedExternalMemberToStorefront,
  type ConsumerSubscriptionFacts
} from "#/services/consumerSubscription/externalStorefront"
import { LoadingInner } from "./Loading"

/**
 * Rendered in place of a page for a LAPSED external-storefront member: sends them to their store (see
 * `sendLapsedExternalMemberToStorefront`) and holds a spinner while the browser leaves. When carrot-app has
 * no url for the store it says so instead — the same words login uses — rather than falling through to a
 * Stripe page or the paywall, neither of which can renew this member.
 *
 * The hand-off runs once, in an effect, so a re-render neither re-navigates nor re-reports.
 */
export function LapsedExternalStorefrontHandoff({
  subscription
}: {
  subscription: ConsumerSubscriptionFacts | null | undefined
}): JSX.Element {
  const intl = useIntl()
  const [missingUrl, setMissingUrl] = React.useState(false)
  const subscriptionRef = React.useRef(subscription)

  React.useEffect(() => {
    if (sendLapsedExternalMemberToStorefront(subscriptionRef.current) === "missing_url") {
      setMissingUrl(true)
    }
  }, [])

  if (!missingUrl) {
    return <LoadingInner />
  }
  const { message: descriptor, values } = lapsedExternalMemberNoUrlMessage(subscriptionRef.current?.storefront)
  const message = intl.formatMessage(descriptor, values)
  return (
    <AppAccessPageLayout title={intl.formatMessage({ defaultMessage: "Subscription" })}>
      <AppAccessPageCard showLocaleSelector={false}>
        <Box textAlign="center">
          <Typography>{message}</Typography>
        </Box>
      </AppAccessPageCard>
    </AppAccessPageLayout>
  )
}
